// Dependencies
import { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import ProgressiveImage from "react-progressive-image";
import XTimeline from "./XTimeline";
import { Dropdown } from "semantic-ui-react";

// Child components
import ListProperty from "./ListProperty";
import PaginationDB from "./PaginationDB";

// Styles
import { Grid, Container, layoutWidth, layoutPadding } from "../styles/Layout";
import { Button } from "../styles/Button";
import { CheckboxWrap, CheckboxLabel, CheckboxInput, CheckboxBox } from "../styles/Checkbox";
import { RadioWrap, RadioLabel, RadioInput, RadioBox } from "../styles/RadioButton";
import { DropdownSelect, DropdownWrap, ArrowDown, Option } from "../styles/Dropdown";
import { mapStyles } from "../../assets/other/mapStyles";
import { Link, useHistory } from "react-router-dom";
import "../styles/semantic.min.css";
import { size } from "../styles/Breakpoints";

// Icons & Images
import { LaunchRounded, FirstPage } from "@material-ui/icons";
import Loader from "react-loader-spinner";

// Data
import { Locations } from "../../data/Locations";
import { propertyAltBase } from "../../data/seo";
import { OFFICE } from "../../data/structuredData";
import useGoogleMaps from "../../hooks/useGoogleMaps";
import useNearViewport from "../../hooks/useNearViewport";
import { priceRangeSales, priceRangeLettings } from "../../data/PriceRange";
import { bedroomRange } from "../../data/BedroomRange";
import axios from "axios";
import { LoaderWrapper } from "../styles/Loader";

/*
 * The search panel, map and results list each sit in their own card. The
 * section behind them is tinted, not white, or the shadows would barely show.
 */
const card = css`
    background: white;
    border-radius: 14px;
    box-shadow: 0 2px 12px rgba(20, 49, 91, 0.08);
`;

const Wrapper = styled.section`
    background: #f4f7fb;
    padding: 2.5rem 0 3rem;
    -webkit-box-sizing: inherit;
    box-sizing: inherit;
    z-index: -1;
    @media screen and (max-width: ${size.sm}px) {
        padding: 1rem 0 2rem;
    }
`;

/*
 * The map keeps its original 2fr width and is square (see MapArea), so its
 * height follows that width. The search panel keeps its 1fr width and
 * stretches to the same height.
 */
const FilterAndMapWrapper = styled(Container)`
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 1.5rem;
    box-sizing: inherit;
    @media screen and (max-width: ${size.sm}px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

// FILTER PANEL
/*
 * Content starts at the top, level with the map beside it. It used to be
 * centred, which left a tall gap above the heading now the card is as tall
 * as the square map; the X timeline fills what is left below the buttons.
 */
const FilterPanelForm = styled.form`
    ${card}
    height: 100%;
    min-height: 25rem;
    display: inherit;
    align-content: start;
    @media screen and (max-width: ${size.sm}px) {
        min-height: 0;
    }
`;

const FilterPanel = styled.div`
    /* Column, so the timeline below the buttons can take the space left. */
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 2rem;
    -webkit-box-sizing: inherit;
    box-sizing: inherit;
`;

const Title = styled.h3`
    margin-top: 0;
`;

// Other styles

const Seperator = styled.hr`
    margin-top: 1rem;
    margin-bottom: 0;
`;

const margin = {
    marginTop: "1rem",
};

/* Light blue like the hero's CTAs; Button's own colour is white, which this
   background is too pale for. */
const DataButton = styled(Button)`
    color: ${(props) => props.theme.darkblue};
`;

/*
 * The X timeline fills the space the search panel has left over next to the
 * square map. Hidden on mobile, where the panel is already the right height
 * and the timeline would just be a long scroll before the map.
 */
const TimelineWrap = styled.div`
    margin-top: 1.25rem;
    border-radius: 10px;
    overflow: hidden;
    /*
     * Fills whatever is left of the card below the buttons, so it lines up
     * with the bottom of the map at any width. X sizes its own iframe from
     * data-height, so that is overridden to match.
     */
    flex: 1;
    min-height: 12rem;
    & iframe {
        height: 100% !important;
    }
    /*
     * Until X's script swaps it for the timeline - and permanently, for anyone
     * whose browser blocks the embed - the anchor shows as a plain link, so it
     * is styled to read as one rather than as stray text.
     */
    & a.twitter-timeline {
        display: inline-block;
        font-size: 0.85rem;
        font-weight: 500;
        color: ${(props) => props.theme.darkblue};
        text-decoration: underline;
    }
    @media screen and (max-width: ${size.sm}px) {
        display: none;
    }
`;

// Map styles

/*
 * A flex column so the Lettings/Sales toggles can sit inside the card: laid
 * over the map's bottom-left corner on desktop, and as a bar under the map on
 * mobile, where they used to hang below the panel.
 */
const MapPanel = styled.div`
    ${card}
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    align-self: start;
    & > ${CheckboxWrap} {
        left: 0.75rem;
        bottom: 0.75rem;
        margin: 0;
    }
    @media screen and (max-width: ${size.sm}px) {
        min-height: 0;
        & > ${CheckboxWrap} {
            position: static;
            width: auto;
        }
    }
`;

/* Square, so the card matches the rounded tiles elsewhere on the page. */
const MapArea = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    aspect-ratio: 1 / 1;
`;

const mapContainerStyle = {
    width: "100%",
    height: "100%",
};

const MarkerInfoWrap = styled.div`
    width: 10rem;
    max-height: 10rem;
    position: relative;
    overflow: hidden;
`;

const MarkerImg = styled.img`
    width: 100%;
    object-fit: fill;
`;

const MarkerTitle = styled.h3`
    margin-top: 0;
`;
const MarkerPrice = styled.p`
    margin: -0.75rem 0 0 0;
`;

/*
 * Demoted from h1 to h2. The home page had two h1s - the hero headline and
 * this filter heading - which leaves a search engine no single statement of
 * what the page is about. The hero keeps the h1; this is a section heading.
 * Rendering is pinned to what it was (36px / 400 / 1px tracking).
 */
const FilterTitle = styled.h2`
    text-align: center;
    color: ${(props) => props.theme.darkblue};
    margin: 0;
    /* was h1 */
    font-size: 2rem;
    font-weight: 400;
    letter-spacing: 1px;
    line-height: normal;
    @media screen and (max-width: ${size.sm}px) {
        margin: 0 0 1rem 0;
    }
`;

const StyledDropdown = styled(Dropdown)`
    margin-top: 1rem;
    padding: 0.75rem 1rem 0.75rem 1rem;
    width: 100%;
    & * {
        font-size: 0.9rem;
    }
    & i {
        display: none;
    }
`;

const NoPropertiesText = styled.p`
    text-align: center;
    margin-bottom: -1rem;
    padding: 4rem 0 7rem 0;
`;

const PropertiesListCard = styled.div`
    ${card}
    margin-top: 1.5rem;
    padding: 0.5rem 2rem 2rem;
    @media screen and (max-width: ${size.sm}px) {
        margin-top: 1rem;
        padding: 0 0 1rem;
    }
`;

const PropertiesList = styled.div`
    padding-top: 1rem;
`;

const SearchLoader = styled(Loader)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FilterTopRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 1.5rem 0 1rem;
    border-bottom: 1px solid ${(props) => props.theme.lightblue};
    @media screen and (max-width: ${size.sm}px) {
        display: block;
        padding: 1rem;
    }
`;

const ReturnBtn = styled.div`
    padding: 0;
    align-self: center;
    font-size: 0.7rem;
    font-weight: 500;
    cursor: pointer;
    color: ${(props) => props.theme.darkblue};
    transition: 0.3s;
    &:hover {
        color: ${(props) => props.theme.lightblue};
        transition: 0.3s;
    }
    & svg {
        margin-bottom: -0.5rem;
        width: 1.25rem;
    }
`;

const markerLinkStyle = {
    margin: "-0.25rem 0 0.25rem 0",
    fontSize: ".7rem",
    fontWeight: "500",
    textDecoration: "underline",
};

// Dropdown search
const areaOptions = Locations.map((area, index) => ({
    key: index,
    text: area,
    value: area,
}));

// MAP Options & functions
const options = {
    styles: mapStyles,
    disableDefaultUI: true,
    zoomControl: true,
    scrollwheel: false,
};

export default function SearchFilter({ properties }) {
    // MAP STATES
    const [propertyType, setPropertyType] = useState(["Sales", "Lettings"]);
    const [lettingsCheck, setLettingsCheck] = useState(false);
    const [salesCheck, setSalesCheck] = useState(false);
    const [filterResultsCheck, setFilterResultsCheck] = useState(false);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [mapProperties, setMapProperties] = useState(properties);

    // Centred on the Tooting High Street office, the same point the contact
    // page's map pins, rather than a generic SW London coordinate.
    const [currentMapCenter, setMapCenter] = useState({ lat: OFFICE.lat, lng: OFFICE.lng });

    // FILTER STATES
    const [radioSelection, setRadioSelection] = useState("Lettings");
    const [locationDropdown, setLocationDropdown] = useState("Any Location");
    const [bedroomsDropdown, setBedroomsDropdown] = useState("Any Bedrooms");
    const [priceDropdown, setPriceDropdown] = useState("No Max Price");
    const [searched, setSearched] = useState(false);
    const [filterTitle, setFilterTitle] = useState("Recently Added Properties");
    const [isSearching, setIsSearching] = useState(false);
    const [showPropertyTypeDropdown, setShowPropertyTypeDropdown] = useState(true);

    // FILTER LIST STATES
    const [listedProperties, setListedProperties] = useState(properties);
    const [propertyListLoading, setPropertyListLoading] = useState(true);
    const [propertyTypeDropdown, setPropertyTypeDropdown] = useState("available");

    // DB pagination states for properties
    const [numberOfPropertyPages, setNumberOfPropertyPages] = useState(0);
    const [propertiesPageNumber, setPropertiesPageNumber] = useState(0);
    const [showPropertyPagination, setShowPropertyPagination] = useState(true);

    // Reset Filter
    const [resetFilter, setResetFilter] = useState(1);
    const history = useHistory();

    // Maps loads on demand now - render nothing until it is ready,
    // because the marker icons below build window.google objects.
    // The map sits well below the fold, so the ~400KB API only downloads once
    // the map panel is near the viewport.
    const mapPanelRef = useRef(null);
    const mapNearViewport = useNearViewport(mapPanelRef);
    const mapsReady = useGoogleMaps(mapNearViewport);

    // Fill pagination array with the number of pages fetched from the DB
    const propertyPages = new Array(numberOfPropertyPages).fill(null).map((v, i) => i);

    // Configure axios proxy
    const axiosInstance = axios.create({ baseURL: process.env.REACT_APP_API_URL });

    // Get properties and paginate them
    const fetchProperties = async (propertyType, pageNumber) => {
        setPropertyTypeDropdown(propertyType);
        setPropertyListLoading(true);
        await axiosInstance.get(`/properties/${propertyType}?page=${pageNumber}`).then((res) => {
            setNumberOfPropertyPages(res.data.totalPages);
            setListedProperties(res.data.properties);
            setPropertyListLoading(false);
        });

        // Set correct title for the list
        switch (propertyType) {
            case "let":
                setFilterTitle("Let properties");
                break;
            case "sold":
                setFilterTitle("Sold properties");
                break;
            default:
                setFilterTitle("Recently Added Properties");
                break;
        }
    };

    useEffect(() => {
        switch (propertyTypeDropdown) {
            case "let":
                fetchProperties("let", propertiesPageNumber);
                break;
            case "sold":
                fetchProperties("sold", propertiesPageNumber);
                break;
            default:
                fetchProperties("available", propertiesPageNumber);
        }
    }, [propertiesPageNumber]);

    const handleLettingsCheckbox = () => {
        setLettingsCheck(!lettingsCheck);
        let newStateArray = propertyType.slice();

        if (lettingsCheck) {
            newStateArray.push("Lettings");

            // Remove Filter Results from array
            newStateArray = newStateArray.filter((val) => val !== "FilterResults");

            setPropertyType(newStateArray);
        } else {
            newStateArray = newStateArray.filter((val) => val !== "Lettings");
            setPropertyType(newStateArray);
        }

        // Remove filter results
        setFilterResultsCheck(false);
    };

    const handleSalesCheckbox = () => {
        setSalesCheck(!salesCheck);
        let newStateArray = propertyType.slice();

        if (salesCheck) {
            newStateArray.push("Sales");

            // Remove Filter Results from array
            newStateArray = newStateArray.filter((val) => val !== "FilterResults");

            setPropertyType(newStateArray);
        } else {
            newStateArray = newStateArray.filter((val) => val !== "Sales");
            setPropertyType(newStateArray);
        }

        // Remove filter results
        setFilterResultsCheck(false);
    };

    const handleFilterResultsCheckbox = () => {
        setFilterResultsCheck(!filterResultsCheck);

        if (!filterResultsCheck) {
            // Disable other filters
            setSalesCheck(true);
            setLettingsCheck(true);
            setPropertyType(["FilterResults"]);
        } else {
            // Enable other filters
            setSalesCheck(false);
            setLettingsCheck(false);
            setPropertyType(["Sales", "Lettings"]);
        }
    };

    // FILTER HANDLERS
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSearching(true);
        setPropertyTypeDropdown("available");

        let results = properties;

        // Check location selection (Any/Selected)
        if (locationDropdown !== "Any Location") {
            results = results.filter((select) => select.location === locationDropdown);
        }
        // else {
        //     setPropertyListLoading(true);
        //     await axiosInstance.get(`/properties/available`).then((res) => {
        //         setNumberOfPropertyPages(res.data.totalPages);
        //         results = res.data.properties; // properties sorted by date at the backend
        //         setPropertyListLoading(false);
        //     });
        // }

        // Check property type selection (rent/buy corresponds to letting/sale)
        results = results.filter((select) => select.category === radioSelection);

        // Check bedrooms selection (Any/Selected)
        if (bedroomsDropdown !== "Any Bedrooms") {
            results = results.filter((select) => select.bedrooms === parseInt(bedroomsDropdown));
        }

        // Check price selection (Any/Selected)
        if (priceDropdown !== "No Max Price") {
            results = results.filter((select) => select.price <= parseInt(priceDropdown));
        }

        // Sort results by date
        results = results.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
        setPropertyType(["FilterResults"]);
        setFilterResultsCheck(true);
        setSalesCheck(true);
        setLettingsCheck(true);
        setListedProperties(results);
        setSearched(true); // Display filter checkbox after search
        setShowPropertyPagination(false);
        setShowPropertyTypeDropdown(false);

        // Set filter title
        const whatType = radioSelection === "Sales" ? "Buy" : "Rent";
        setFilterTitle(`Properties to ${whatType} in ${locationDropdown}`);
        setTimeout(() => setIsSearching(false), 250); // Add a little spinner to the button to simulate search
    };

    const handleRadio = (e) => {
        setRadioSelection(e.target.value);
    };

    // Dropdown search handling
    const handleLocationDropdown = (event, { value }) => {
        setLocationDropdown(value);
    };

    const { value } = locationDropdown;

    const handleBedroomsDropdown = (e) => {
        setBedroomsDropdown(e.target.value);
    };

    const handlePriceDropdown = (e) => {
        setPriceDropdown(e.target.value);
    };

    const handlePageNumberClick = (e) => {
        document.getElementById("root").scrollTo({ top: 600, left: 0 });
        setPropertiesPageNumber(e.target.value);
    };

    const handlePropertyTypeDropdown = async (e) => {
        const selection = e.target.value;
        switch (selection) {
            case "let":
                fetchProperties("let", propertiesPageNumber);
                break;
            case "sold":
                fetchProperties("sold", propertiesPageNumber);
                break;
            default:
                fetchProperties("available", propertiesPageNumber);
        }
        setRadioSelection("Lettings");
        setLocationDropdown("Any Location");
        setBedroomsDropdown("Any Bedrooms");
        setPriceDropdown("No Max Price");
        // setSearched(false);
        setShowPropertyPagination(true);
        setPropertiesPageNumber(0);
    };

    const handleFilterReset = () => {
        history.push("/reset");
        setTimeout(() => {
            history.push("/");
        }, 0);
    };

    return (
        <Wrapper>
            <FilterAndMapWrapper>
                {/* FILTER PANEL */}
                <FilterPanelForm onSubmit={handleSubmit}>
                    <FilterPanel>
                        <Title>Find your dream property today!</Title>
                        <RadioWrap>
                            <RadioInput
                                type="radio"
                                id="lettings"
                                name="property_type"
                                value="Lettings"
                                checked={radioSelection === "Lettings"}
                                onChange={handleRadio}
                                hidden
                            />
                            <RadioLabel rent htmlFor="lettings">
                                <RadioBox />
                                RENT
                            </RadioLabel>
                            <RadioInput
                                type="radio"
                                id="sale"
                                name="property_type"
                                value="Sales"
                                checked={radioSelection === "Sales"}
                                onChange={handleRadio}
                                hidden
                            />
                            <RadioLabel buy htmlFor="sale">
                                <RadioBox />
                                BUY
                            </RadioLabel>
                        </RadioWrap>
                        <DropdownWrap style={{ boxSizing: "inherit" }}>
                            <StyledDropdown
                                onChange={handleLocationDropdown}
                                options={areaOptions}
                                value={value}
                                defaultValue={locationDropdown}
                                search
                                selection
                            />
                        </DropdownWrap>
                        <Grid lg={2} gap={"1rem"} style={margin}>
                            <DropdownWrap>
                                <ArrowDown />
                                <DropdownSelect onChange={handleBedroomsDropdown}>
                                    <Option>Any Bedrooms</Option>
                                    {bedroomRange.map((bed, index) => (
                                        <Option key={index} value={bed}>
                                            {bed === 0 ? "Studio" : `${bed} Bedrooms`}
                                        </Option>
                                    ))}
                                </DropdownSelect>
                            </DropdownWrap>
                            <DropdownWrap>
                                <ArrowDown />
                                <DropdownSelect onChange={handlePriceDropdown}>
                                    <Option>No Max Price</Option>
                                    {radioSelection === "Lettings" ? (
                                        <>
                                            {priceRangeLettings.map((price, index) => (
                                                <Option key={index} value={price}>
                                                    £
                                                    {price
                                                        .toString()
                                                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                                    pcm
                                                </Option>
                                            ))}
                                        </>
                                    ) : (
                                        <>
                                            {priceRangeSales.map((price, index) => (
                                                <Option key={index} value={price}>
                                                    £
                                                    {price
                                                        .toString()
                                                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                                </Option>
                                            ))}
                                        </>
                                    )}
                                </DropdownSelect>
                            </DropdownWrap>
                        </Grid>
                        <Button bright style={margin} type="submit">
                            {isSearching ? (
                                <SearchLoader type="Puff" color="white" height={19} width={19} />
                            ) : (
                                "Search"
                            )}
                        </Button>
                        <Seperator />
                        <a
                            href="https://www.onthemarket.com/instant-valuation/property-people/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <Button primary style={margin} type="button">
                                Instant Valuation
                            </Button>
                        </a>
                        <Link to="/property-report">
                            <DataButton light style={margin} type="button">
                                Public data on the property
                            </DataButton>
                        </Link>
                        {/* Same gate as the map: X's widget script is third-party
                            weight, so it waits until this section is scrolled to. */}
                        {mapNearViewport && (
                            <TimelineWrap>
                                <XTimeline screenName="Property_People" height={300} />
                            </TimelineWrap>
                        )}
                    </FilterPanel>
                </FilterPanelForm>

                {/* MAP PANEL */}
                <MapPanel ref={mapPanelRef}>
                    <MapArea>
                        {mapsReady && (
                        <GoogleMap
                            center={currentMapCenter}
                            mapContainerStyle={mapContainerStyle}
                            zoom={13}
                            options={options}
                        >
                            {propertyType.find((name) => name === "Sales") &&
                                mapProperties.map((property, index) => (
                                    <div key={index}>
                                        {property.category === "Sales" && (
                                            <Marker
                                                position={{
                                                    lat: property.coordinates.lat,
                                                    lng: property.coordinates.lng,
                                                }}
                                                icon={{
                                                    url: "/images/pp-pin-light.svg",
                                                    scaledSize: new window.google.maps.Size(40, 40),

                                                    // The below can be deleted if 100% accudate pin pos is desired
                                                    origin: new window.google.maps.Point(0, 0),
                                                    anchor: new window.google.maps.Point(20, 15),
                                                }}
                                                onClick={() => {
                                                    setSelectedMarker(property);
                                                }}
                                            ></Marker>
                                        )}
                                    </div>
                                ))}

                            {propertyType.find((name) => name === "Lettings") &&
                                mapProperties.map((property, index) => (
                                    <div key={index}>
                                        {property.category === "Lettings" && (
                                            <Marker
                                                position={{
                                                    lat: property.coordinates.lat,
                                                    lng: property.coordinates.lng,
                                                }}
                                                icon={{
                                                    url: "/images/pp-pin-letting.svg",
                                                    scaledSize: new window.google.maps.Size(40, 40),

                                                    // The below can be deleted if 100% accudate pin pos is desired
                                                    origin: new window.google.maps.Point(0, 0),
                                                    anchor: new window.google.maps.Point(20, 15),
                                                }}
                                                onClick={() => {
                                                    setSelectedMarker(property);
                                                }}
                                            ></Marker>
                                        )}
                                    </div>
                                ))}

                            {propertyType.find((name) => name === "FilterResults") &&
                                listedProperties.map((property, index) => (
                                    <Marker
                                        position={{
                                            lat: property.coordinates.lat,
                                            lng: property.coordinates.lng,
                                        }}
                                        icon={{
                                            url: "/images/pp-pin-green.svg",
                                            scaledSize: new window.google.maps.Size(40, 40),

                                            // The below can be deleted if 100% accudate pin pos is desired
                                            origin: new window.google.maps.Point(0, 0),
                                            anchor: new window.google.maps.Point(20, 15),
                                        }}
                                        key={index}
                                        onClick={() => {
                                            setSelectedMarker(property);
                                        }}
                                    ></Marker>
                                ))}

                            {selectedMarker && (
                                <InfoWindow
                                    position={selectedMarker.coordinates}
                                    onCloseClick={() => {
                                        setSelectedMarker(null);
                                    }}
                                >
                                    <MarkerInfoWrap>
                                        <MarkerTitle>
                                            {selectedMarker.bedrooms} Bedroom {selectedMarker.type}
                                        </MarkerTitle>
                                        <MarkerPrice>
                                            £
                                            {selectedMarker.price
                                                .toString()
                                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                            {selectedMarker.priceType}
                                        </MarkerPrice>
                                        <Link
                                            to={`/property/${selectedMarker._id}`}
                                            style={markerLinkStyle}
                                        >
                                            View Property
                                        </Link>
                                        <Link
                                            to={`/property/${selectedMarker._id}`}
                                            target="_blank"
                                            style={markerLinkStyle}
                                        >
                                            <span style={{ marginLeft: ".5rem" }}>
                                                New Tab
                                                <LaunchRounded
                                                    style={{
                                                        width: ".8rem",
                                                        position: "absolute",
                                                        margin: "-.3rem 0 0 .2rem",
                                                    }}
                                                />
                                            </span>
                                        </Link>
                                        <ProgressiveImage
                                            src={selectedMarker.photos[0]}
                                            placeholder={"/images/marker-img-placeholder.jpg"}
                                        >
                                            {(src, loading) => (
                                                <MarkerImg
                                                    src={src}
                                                    alt={propertyAltBase(selectedMarker)}
                                                    style={{
                                                        filter: loading ? "blur(2px)" : "blur(0)",
                                                        transition: ".1s",
                                                        marginTop: ".25rem",
                                                    }}
                                                />
                                            )}
                                        </ProgressiveImage>
                                    </MarkerInfoWrap>
                                </InfoWindow>
                            )}
                        </GoogleMap>
                        )}
                    </MapArea>
                    <CheckboxWrap>
                        <CheckboxInput
                            type="checkbox"
                            id="letting"
                            checked={!lettingsCheck}
                            onChange={handleLettingsCheckbox}
                            hidden
                        />
                        <CheckboxLabel lettings htmlFor="letting">
                            <CheckboxBox />
                            LETTINGS
                        </CheckboxLabel>

                        <CheckboxInput
                            type="checkbox"
                            id="sales"
                            checked={!salesCheck}
                            onChange={handleSalesCheckbox}
                            hidden
                        />
                        <CheckboxLabel sales htmlFor="sales">
                            <CheckboxBox />
                            SALES
                        </CheckboxLabel>

                        {searched && (
                            <>
                                <CheckboxInput
                                    filterResults
                                    type="checkbox"
                                    id="filterResults"
                                    checked={filterResultsCheck}
                                    onChange={handleFilterResultsCheckbox}
                                    hidden
                                />
                                <CheckboxLabel filterResults htmlFor="filterResults">
                                    <CheckboxBox />
                                    FILTER RESULTS
                                </CheckboxLabel>
                            </>
                        )}
                    </CheckboxWrap>
                </MapPanel>
            </FilterAndMapWrapper>

            {/* FILTER RESULTS PANEL */}
            <Container>
                <PropertiesListCard>
                    <FilterTopRow>
                        <FilterTitle>{filterTitle}</FilterTitle>
                        {showPropertyTypeDropdown ? (
                            <DropdownWrap narrow>
                                <ArrowDown narrow small />
                                <DropdownSelect small onChange={handlePropertyTypeDropdown}>
                                    <Option value="available">Available</Option>
                                    <Option value="let">Let</Option>
                                    <Option value="sold">Sold</Option>
                                </DropdownSelect>
                            </DropdownWrap>
                        ) : (
                            <ReturnBtn onClick={handleFilterReset}>
                                <FirstPage />
                                Return to default results
                            </ReturnBtn>
                        )}
                    </FilterTopRow>
                    {propertyListLoading ? (
                        <LoaderWrapper style={{ height: "80vh" }}>
                            <Loader type="Puff" color="#A2DEF9" height={60} width={60} />
                        </LoaderWrapper>
                    ) : (
                        <PropertiesList>
                            {listedProperties.map((property, index) => (
                                <div key={index}>
                                    <ListProperty published property={property} />
                                </div>
                            ))}
                            {showPropertyPagination ? (
                                <PaginationDB
                                    pages={propertyPages}
                                    currentPage={propertiesPageNumber}
                                    setPageNumber={handlePageNumberClick}
                                />
                            ) : null}
                            {listedProperties.length === 0 && (
                                <NoPropertiesText>
                                    There were no properties found with your search criteria. Maybe try
                                    expanding your search.
                                </NoPropertiesText>
                            )}
                        </PropertiesList>
                    )}
                </PropertiesListCard>
            </Container>
        </Wrapper>
    );
}
